/**
 * usePartnerCompanies Hooks
 * React Query hooks for partner company management
 * Feature: 028-partner-companies (T027 - US1)
 *
 * Implements:
 * - usePartnerCompanies: List partner companies with pagination and filters
 * - usePartnerCompany: Get single partner company by ID
 * - useCreatePartnerCompany: Create partner company mutation
 * - useUpdatePartnerCompany: Update partner company mutation
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { partnerCompaniesApi } from '../api/partnerCompaniesApi';
import type {
  AuthMethodEntry,
  PartnerCompany,
  PartnerCompanyWithCounts,
  PaginatedResponse,
  CreatePartnerCompanyPayload,
  UpdatePartnerCompanyPayload,
  PartnerCompanyListParams,
} from '../types/partner-company.types';

// Query keys for cache management
export const partnerCompanyKeys = {
  all: ['partner-companies'] as const,
  lists: () => [...partnerCompanyKeys.all, 'list'] as const,
  list: (params: PartnerCompanyListParams) =>
    [...partnerCompanyKeys.lists(), params] as const,
  details: () => [...partnerCompanyKeys.all, 'detail'] as const,
  detail: (id: string) => [...partnerCompanyKeys.details(), id] as const,
  search: (query: string) =>
    [...partnerCompanyKeys.all, 'search', query] as const,
};

const normalizeAuthMethods = (
  authMethods: UpdatePartnerCompanyPayload['authMethods'],
): AuthMethodEntry[] | undefined =>
  authMethods?.map((method) => ({
    method,
    isEnabled: true,
  }));

const mergeDetailWithPayload = (
  previousCompany: PartnerCompanyWithCounts,
  payload: UpdatePartnerCompanyPayload,
): PartnerCompanyWithCounts => {
  const { authMethods, ...partialUpdate } = payload;

  return {
    ...previousCompany,
    ...partialUpdate,
    ...(authMethods
      ? { authMethods: normalizeAuthMethods(authMethods) }
      : {}),
  };
};

const mergeDetailWithResponse = (
  previousCompany: PartnerCompanyWithCounts,
  updatedCompany: PartnerCompany,
): PartnerCompanyWithCounts => ({
  ...previousCompany,
  ...updatedCompany,
});

/**
 * Hook to fetch paginated partner companies list
 *
 * @param params - Query parameters (page, limit, search, isActive)
 * @returns Query result with paginated partner companies
 *
 * Features:
 * - 30s stale time to reduce API calls
 * - keepPreviousData for smooth pagination
 *
 * @example
 * const { data, isLoading } = usePartnerCompanies({
 *   page: 1,
 *   limit: 20,
 *   search: 'acme',
 *   isActive: true
 * });
 */
export function usePartnerCompanies(
  params: PartnerCompanyListParams = {},
): UseQueryResult<PaginatedResponse<PartnerCompany>, Error> {
  return useQuery({
    queryKey: partnerCompanyKeys.list(params),
    queryFn: () => partnerCompaniesApi.getPartnerCompanies(params),
    staleTime: 30000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook to fetch single partner company by ID
 *
 * @param id - Partner company ID
 * @returns Query result with partner company details and user counts
 */
export function usePartnerCompany(
  id: string,
): UseQueryResult<PartnerCompanyWithCounts, Error> {
  return useQuery({
    queryKey: partnerCompanyKeys.detail(id),
    queryFn: () => partnerCompaniesApi.getPartnerCompany(id),
    enabled: !!id,
  });
}

/**
 * Hook to create new partner company
 *
 * @returns Mutation function and state
 *
 * Features:
 * - Invalidates partner companies list on success
 * - Returns created partner company immediately
 *
 * @example
 * const createPartnerCompany = useCreatePartnerCompany();
 *
 * await createPartnerCompany.mutateAsync({
 *   name: 'Acme Corp',
 *   logoUrl: 'https://example.com/logo.png',
 * });
 */
export function useCreatePartnerCompany(): UseMutationResult<
  PartnerCompany,
  Error,
  CreatePartnerCompanyPayload
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: partnerCompaniesApi.createPartnerCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerCompanyKeys.lists() });
    },
  });
}

/**
 * Hook to update existing partner company
 *
 * @returns Mutation function and state
 *
 * Features:
 * - Optimistic update (immediate UI update)
 * - Rollback on error
 * - Updates cache with new data
 *
 * @example
 * const updatePartnerCompany = useUpdatePartnerCompany();
 *
 * await updatePartnerCompany.mutateAsync({
 *   id: 'company_123',
 *   payload: { name: 'Updated Name', isActive: false }
 * });
 */
export function useUpdatePartnerCompany(): UseMutationResult<
  PartnerCompany,
  Error,
  { id: string; payload: UpdatePartnerCompanyPayload }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) =>
      partnerCompaniesApi.updatePartnerCompany(id, payload),

    // Optimistic update
    onMutate: async ({ id, payload }) => {
      await queryClient.cancelQueries({
        queryKey: partnerCompanyKeys.detail(id),
      });

      const previousCompany = queryClient.getQueryData<PartnerCompanyWithCounts>(
        partnerCompanyKeys.detail(id),
      );

      if (previousCompany) {
        queryClient.setQueryData<PartnerCompanyWithCounts>(
          partnerCompanyKeys.detail(id),
          mergeDetailWithPayload(previousCompany, payload),
        );
      }

      return { previousCompany };
    },

    // Rollback on error
    onError: (_error, { id }, context) => {
      if (context?.previousCompany) {
        queryClient.setQueryData(
          partnerCompanyKeys.detail(id),
          context.previousCompany,
        );
      }
    },

    // Refetch on success
    onSuccess: (data, { id }) => {
      const previousCompany = queryClient.getQueryData<PartnerCompanyWithCounts>(
        partnerCompanyKeys.detail(id),
      );

      if (previousCompany) {
        queryClient.setQueryData<PartnerCompanyWithCounts>(
          partnerCompanyKeys.detail(id),
          mergeDetailWithResponse(previousCompany, data),
        );
      }

      queryClient.invalidateQueries({ queryKey: partnerCompanyKeys.lists() });
    },
  });
}
